import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Sparkles } from "lucide-react";
import { logger } from "@/lib/logger";

const KEY = "wynridge_banner_dismissed_v2";
const REMEMBER_DAYS = 14;

/**
 * Slim dismissible announcement banner. Renders at the very top of the page
 * above the nav. Remembers dismissal for 14 days via localStorage.
 */
export default function AnnouncementBanner({
  message = "Get a fast online estimate — start here",
  ctaText = "Start Now",
  ctaHref = "/pressure-washing-estimate-calculator/",
}) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(KEY);
      if (!raw) return setVisible(true);
      const { expiresAt } = JSON.parse(raw);
      if (!expiresAt || Date.now() > expiresAt) return setVisible(true);
    } catch (e) {
      logger.warn("AnnouncementBanner localStorage read failed:", e);
      setVisible(true);
    }
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(
        KEY,
        JSON.stringify({ expiresAt: Date.now() + REMEMBER_DAYS * 24 * 60 * 60 * 1000 })
      );
    } catch (e) {
      logger.warn("AnnouncementBanner localStorage write failed:", e);
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="announcement-banner"
      className="relative z-[55] bg-electric-500 text-white"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-2 flex items-center gap-3 text-sm">
        <Sparkles className="w-4 h-4 flex-shrink-0" />
        <span className="flex-1 font-semibold truncate">{message}</span>
        <Link
          to={ctaHref}
          data-testid="announcement-banner-cta"
          className="hidden sm:inline-flex items-center rounded-full bg-white/20 hover:bg-white/30 px-3 py-1 text-xs font-bold border border-white/30 transition-colors"
        >
          {ctaText}
        </Link>
        <button
          type="button"
          onClick={dismiss}
          data-testid="announcement-banner-close"
          className="p-1 rounded-full hover:bg-white/20 transition-colors flex-shrink-0"
          aria-label="Dismiss announcement"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
